import React, { useState, useEffect } from "react";
import {
  getAllAssetSubCategories,
  createAssetSubCategory,
  updateAssetSubCategory,
  exportAssetSubCategories,
  getAllAssetCategories,
} from "../../services/CatagoryServices";
import { HiOutlineFolderOpen } from "react-icons/hi";
import {
  FaRegEdit,
  FaClipboardList,
  FaPlusCircle,
  FaTrash,
} from "react-icons/fa";
import { IoMdAdd } from "react-icons/io";
import Select from "react-select";
import { CustomTooltip } from "../../components/CustomTooltip";
import { MultiActionDropdown } from "../../components";
import { MdAddchart } from "react-icons/md";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  InputGroup,
  FormControl,
  Badge,
  Spinner,
  Alert,
} from "react-bootstrap";
import Pagination from "../Constant/Pagination";
import { FiDownload } from "react-icons/fi";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import { toast } from "react-toastify";

const SubcategoryManagement = () => {
  // State for subcategories
  const [subcategories, setSubcategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [subcategoryLoading, setSubcategoryLoading] = useState(false);
  const [subcategorySearch, setSubcategorySearch] = useState("");
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showBulkSubcategoryModal, setShowBulkSubcategoryModal] =
    useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [subcategoryFormData, setSubcategoryFormData] = useState({
    name: "",
    category: "",
    is_active: true,
  });
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [subcategoryErrors, setSubcategoryErrors] = useState({});
  const [subcategoryPage, setSubcategoryPage] = useState(1);
  const [subcategoryCount, setSubcategoryCount] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const subcategoryPageSize = 10;

  // Bulk form states
  const [bulkSubcategoryFormData, setBulkSubcategoryFormData] = useState([
    { name: "", category: "", is_active: true },
  ]);
  const statusOptions = [
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];
  const [bulkUploadErrors, setBulkUploadErrors] = useState({});

  // Fetch all categories for dropdown
  const fetchAllCategories = async () => {
    try {
      const response = await getAllAssetCategories({ page_size: 1000 });
      setAllCategories(response.data.results || []);
    } catch (error) {
      console.error("Failed to fetch all categories:", error);
    }
  };

  // Fetch subcategories
  const fetchSubcategories = async () => {
    setSubcategoryLoading(true);
    try {
      const params = {
        page: subcategoryPage,
        page_size: subcategoryPageSize,
      };
      if (subcategorySearch) params.search = subcategorySearch;
      if (categoryFilter) params.category_id = categoryFilter;
      if (statusFilter !== null) params.is_active = statusFilter;

      const response = await getAllAssetSubCategories(params);

      // Handle the nested response structure
      const responseData = response.data?.results?.results || [];
      const responseCount = response.data?.results?.count || 0;

      setSubcategories(responseData);
      setSubcategoryCount(responseCount);
    } catch (error) {
      console.error("Failed to fetch subcategories:", error);
    } finally {
      setSubcategoryLoading(false);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchAllCategories();
    fetchSubcategories();
  }, [subcategorySearch, subcategoryPage, categoryFilter, statusFilter]);

  const categoryOptions = [
    ...allCategories.map((category) => ({
      value: category.id,
      label: category.name,
    })),
  ];

  // Subcategory handlers
  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubcategory) {
        await updateAssetSubCategory(
          editingSubcategory.id,
          subcategoryFormData
        );
        toast.success("Subcategory updated successfully");
      } else {
        await createAssetSubCategory(subcategoryFormData);
        toast.success("Subcategory created successfully");
      }
      setShowSubcategoryModal(false);
      setEditingSubcategory(null);
      setSubcategoryFormData({ name: "", category: "", is_active: true });
      fetchSubcategories();
    } catch (error) {
      if (error.response?.data) {
        setSubcategoryErrors(error.response.data);
      }
    }
  };

  const handleEditSubcategory = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryFormData({
      name: subcategory.name,
      category: subcategory.category?.id || subcategory.category || "",
      is_active: subcategory.is_active,
    });
    setShowSubcategoryModal(true);
  };

  const handleSubcategoryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSubcategoryFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setSubcategoryErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Bulk subcategory handlers
  const handleBulkSubcategoryChange = (index, e) => {
    const { name, value, type, checked } = e.target;
    const updatedBulkData = [...bulkSubcategoryFormData];
    updatedBulkData[index] = {
      ...updatedBulkData[index],
      [name]: type === "checkbox" ? checked : value,
    };
    setBulkSubcategoryFormData(updatedBulkData);
  };

  const handleExportSubcategories = async () => {
    try {
      const params = {};
      if (subcategorySearch) params.search = subcategorySearch;
      if (categoryFilter) params.category_id = categoryFilter;
      if (statusFilter !== null) params.is_active = statusFilter;
      params.export = "excel"; // Ensure export flag is set

      const response = await exportAssetSubCategories(params);

      // Build blob from response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Extract filename from Content-Disposition header
      const disposition = response.headers["content-disposition"];
      const fileName =
        disposition?.split("filename=")[1]?.replace(/["']/g, "") ||
        "asset_subcategories.xlsx";

      // Create object URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Cleanup
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const addBulkSubcategoryRow = () => {
    setBulkSubcategoryFormData([
      ...bulkSubcategoryFormData,
      { name: "", category: "", is_active: true },
    ]);
  };

  const removeBulkSubcategoryRow = (index) => {
    const updatedBulkData = bulkSubcategoryFormData.filter(
      (_, i) => i !== index
    );
    setBulkSubcategoryFormData(updatedBulkData);
  };

  const handleBulkSubcategorySubmit = async (e) => {
    e.preventDefault();
    try {
      // Filter out empty rows and rows without category
      const validData = bulkSubcategoryFormData.filter(
        (item) => item.name.trim() !== "" && item.category !== ""
      );

      if (validData.length === 0) {
        setBulkUploadErrors({
          non_field_errors: [
            "Please add at least one valid subcategory with a category selected",
          ],
        });
        return;
      }

      await createAssetSubCategory(validData);
      setShowBulkSubcategoryModal(false);
      setBulkSubcategoryFormData([{ name: "", category: "", is_active: true }]);
      fetchSubcategories();
    } catch (error) {
      if (error.response?.data) {
        setBulkUploadErrors(error.response.data);
      }
    }
  };

  const StatusBadge = ({ isActive }) => (
    <span
      className={`badge badge-sm ${
        isActive ? "text-bg-success-soft" : "text-bg-danger-soft"
      }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );

  const subcategoryColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => category?.name || "N/A",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "status",
      render: (isActive) => <StatusBadge isActive={isActive} />,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <CustomTooltip title="Edit " arrow placement="top">
          <Button
            variant="inline-primary"
            size="sm"
            onClick={() => handleEditSubcategory(record)}
          >
            <FaRegEdit />
          </Button>
        </CustomTooltip>
      ),
    },
  ];

  const renderTable = (
    data,
    columns,
    loading,
    count,
    page,
    pageSize,
    onPageChange
  ) => {
    // Calculate start and end indices
    const start = count === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, count);

    return (
      <>
        <div className="table-responsive">
          <table className="table fs-6 mb-0 align-middle">
            <thead className="table-light custom-thead">
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {col.render
                        ? col.render(row[col.dataIndex], row)
                        : row[col.dataIndex]}
                    </td>
                  ))}
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    {loading ? "Loading..." : "No data found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Info Area - Fixed version */}
        <div className="d-flex justify-content-between align-items-center px-2 py-2">
          {/* Left Side: Info */}
          <div className="pagination_info small text-muted">
            {count > 0
              ? `Showing ${start} to ${end} of ${count} records`
              : "No records found"}
          </div>

          {/* Right Side: Pagination */}
          {count > 0 && (
            <Pagination
              currentPage={page}
              recordsPage={pageSize}
              dataLength={count}
              handlePagination={(selectedObj) =>
                onPageChange(selectedObj.selected + 1)
              }
              showPageInfo={false}
            />
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="card p-2 mb-2">
        <div className="d-flex flex-column flex-md-row  justify-content-between align-items-center gap-1">
          <div className="flex-grow-1 flex-grow-md-0">
            <h5 className="d-flex align-items-center gap-1 mb-0">
              <HiOutlineFolderOpen size={16} className="text-primary"/>
              Sub-Categories
            </h5>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div>
              <DebounceSmallSearchInput
                placeholder="Search subcategories"
                handleSearchChange={(value) => setSubcategorySearch(value)}
              />
            </div>

            {/* Category Dropdown */}
            <GlobalSearchableDropdown
              options={categoryOptions}
              selected={
                categoryFilter !== null
                  ? categoryOptions.find(
                      (option) => option.value === categoryFilter
                    )
                  : null
              }
              onSelect={(selectedOption) => {
                setCategoryFilter(selectedOption ? selectedOption.value : null);
              }}
              placeholder="Category"
              isClearable={true}
            />

            {/* Status Dropdown */}
            <GlobalSearchableDropdown
              options={statusOptions}
              selected={
                statusFilter !== null
                  ? statusOptions.find(
                      (option) => option.value === statusFilter
                    )
                  : null
              }
              onSelect={(selectedOption) => {
                setStatusFilter(selectedOption ? selectedOption.value : null);
              }}
              placeholder="Status"
              isClearable={true}
            />
            <MultiActionDropdown
              variant="outline-primary"
              btnIcon={<MdAddchart />}
              btnText="Add"
              btnOnClick={() => {
                setEditingSubcategory(null);
                setSubcategoryFormData({
                  name: "",
                  category: "",
                  is_active: true,
                });
                setShowSubcategoryModal(true);
              }}
              menuOptions={[
                {
                  label: "Single Subcategory",
                  icon: <FaPlusCircle />,
                  onClick: () => {
                    setEditingSubcategory(null);
                    setSubcategoryFormData({
                      name: "",
                      category: "",
                      is_active: true,
                    });
                    setShowSubcategoryModal(true);
                  },
                },
                {
                  label: "Bulk Subcategories",
                  icon: <FaClipboardList />,
                  onClick: () => {
                    setBulkSubcategoryFormData([
                      { name: "", category: "", is_active: true },
                    ]);
                    setBulkUploadErrors({});
                    setShowBulkSubcategoryModal(true);
                  },
                },
                {
                  label: "Export to Excel",
                  icon: <FiDownload />,
                  onClick: handleExportSubcategories,
                },
              ]}
            />
          </div>
        </div>
      </div>
      <Card>
        <Card.Body className="p-0">
          {subcategoryLoading ? (
            <div className="text-center p-3">
              <Spinner animation="border" />
            </div>
          ) : (
            renderTable(
              subcategories,
              subcategoryColumns,
              subcategoryLoading,
              subcategoryCount,
              subcategoryPage,
              subcategoryPageSize,
              setSubcategoryPage
            )
          )}
        </Card.Body>

        {/* Single Subcategory Modal */}
        <Modal
          show={showSubcategoryModal}
          onHide={() => setShowSubcategoryModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingSubcategory ? "Edit Subcategory" : "Add New Subcategory"}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubcategorySubmit}>
            <Modal.Body>
              {subcategoryErrors.non_field_errors && (
                <Alert variant="danger">
                  {subcategoryErrors.non_field_errors.map((err, idx) => (
                    <div key={idx}>{err}</div>
                  ))}
                </Alert>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Select
                  options={allCategories.map((category) => ({
                    value: category.id,
                    label: category.name,
                  }))}
                  value={
                    subcategoryFormData.category
                      ? {
                          value: subcategoryFormData.category,
                          label:
                            allCategories.find(
                              (c) => c.id === subcategoryFormData.category
                            )?.name || "Select a category",
                        }
                      : null
                  }
                  onChange={(selectedOption) => {
                    setSubcategoryFormData((prev) => ({
                      ...prev,
                      category: selectedOption ? selectedOption.value : "",
                    }));
                    setSubcategoryErrors((prev) => ({
                      ...prev,
                      category: undefined,
                    }));
                  }}
                  isClearable
                  isSearchable
                  placeholder="Search or select a category"
                  className={subcategoryErrors.category ? "is-invalid" : ""}
                  classNamePrefix="select"
                  filterOption={(option, inputValue) =>
                    option.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      maxHeight: "300px",
                      overflowY: "auto",
                    }),
                    control: (provided) => ({
                      ...provided,
                      minHeight: "38px",
                    }),
                  }}
                  menuPlacement="auto"
                  menuPosition="fixed"
                  menuShouldScrollIntoView={true}
                />
                {subcategoryErrors.category && (
                  <div
                    className="invalid-feedback"
                    style={{ display: "block" }}
                  >
                    {subcategoryErrors.category}
                  </div>
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Subcategory Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={subcategoryFormData.name}
                  onChange={handleSubcategoryChange}
                  isInvalid={!!subcategoryErrors.name}
                  placeholder="Enter subcategory name"  
                />
                <Form.Control.Feedback type="invalid">
                  {subcategoryErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="subcategory-active"
                  label="Active"
                  name="is_active"
                  checked={subcategoryFormData.is_active}
                  onChange={handleSubcategoryChange}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowSubcategoryModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingSubcategory ? "Update" : "Create"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Bulk Subcategory Modal */}
        <Modal
          show={showBulkSubcategoryModal}
          onHide={() => setShowBulkSubcategoryModal(false)}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Bulk Add Subcategories</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleBulkSubcategorySubmit}>
            <Modal.Body>
              {bulkUploadErrors.non_field_errors && (
                <Alert variant="danger">
                  {bulkUploadErrors.non_field_errors.map((err, idx) => (
                    <div key={idx}>{err}</div>
                  ))}
                </Alert>
              )}

              <div className="mb-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={addBulkSubcategoryRow}
                  className="mb-2"
                >
                  <IoMdAdd /> Add Row
                </Button>

                <Table bordered>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkSubcategoryFormData.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <Form.Control
                            type="text"
                            name="name"
                            value={item.name}
                            onChange={(e) =>
                              handleBulkSubcategoryChange(index, e)
                            }
                            placeholder="Subcategory name"
                            isInvalid={!!bulkUploadErrors[index]?.name}
                          />
                          {bulkUploadErrors[index]?.name && (
                            <Form.Control.Feedback type="invalid">
                              {bulkUploadErrors[index].name}
                            </Form.Control.Feedback>
                          )}
                        </td>
                        <td>
                          <Select
                            options={allCategories.map((category) => ({
                              value: category.id,
                              label: category.name,
                            }))}
                            value={
                              item.category
                                ? {
                                    value: item.category,
                                    label:
                                      allCategories.find(
                                        (c) => c.id === item.category
                                      )?.name || "Select category",
                                  }
                                : null
                            }
                            onChange={(selectedOption) => {
                              const updatedBulkData = [
                                ...bulkSubcategoryFormData,
                              ];
                              updatedBulkData[index] = {
                                ...updatedBulkData[index],
                                category: selectedOption
                                  ? selectedOption.value
                                  : "",
                              };
                              setBulkSubcategoryFormData(updatedBulkData);
                            }}
                            isClearable
                            isSearchable
                            placeholder="Search or select category"
                            classNamePrefix="select"
                            filterOption={(option, inputValue) =>
                              option.label
                                .toLowerCase()
                                .includes(inputValue.toLowerCase())
                            }
                            styles={{
                              menu: (provided) => ({
                                ...provided,
                                maxHeight: "300px",
                                overflowY: "auto",
                              }),
                              control: (provided) => ({
                                ...provided,
                                minHeight: "38px",
                              }),
                            }}
                            menuPlacement="auto"
                            menuPosition="fixed"
                          />
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            id={`subcategory-active-${index}`}
                            name="is_active"
                            checked={item.is_active}
                            onChange={(e) =>
                              handleBulkSubcategoryChange(index, e)
                            }
                            label={item.is_active ? "Active" : "Inactive"}
                          />
                        </td>
                        <td>
                          {bulkSubcategoryFormData.length > 1 && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeBulkSubcategoryRow(index)}
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowBulkSubcategoryModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create All
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Card>
    </>
  );
};

export default SubcategoryManagement;
